import { createTheme } from "@mui/material/styles";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import * as d3 from "d3";

export const ARAGON_COLORS = Object.freeze({
  burgundy: "#4A0315",
  navy: "#0D1941",
  steel: "#4A6180",
  wine: "#3D1F28",
  ivory: "#E0D9C7",
  sellerSidebar: "#FFFFFF",
  sellerCanvas: "#F0F2EF",
});

export const ARAGON_FONTS = Object.freeze({
  primary: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
  secondary: '"Be Vietnam Pro", Inter, "Helvetica Neue", Arial, sans-serif',
});

const ARAGON_FONT_STYLESHEET = "https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:wght@400;500;600;700;800&family=Cormorant+Garamond:wght@400;500;600;700&display=swap";

export function AragonFontLoader() {
  useEffect(() => {
    if (document.head.querySelector('link[data-aragon-fonts="true"]')) {
      return undefined;
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = ARAGON_FONT_STYLESHEET;
    link.dataset.aragonFonts = "true";
    document.head.appendChild(link);
    return undefined;
  }, []);
  return null;
}

export const aragonTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: ARAGON_COLORS.navy, contrastText: ARAGON_COLORS.ivory },
    secondary: { main: ARAGON_COLORS.burgundy, contrastText: ARAGON_COLORS.ivory },
    background: { default: ARAGON_COLORS.ivory, paper: ARAGON_COLORS.ivory },
    text: { primary: ARAGON_COLORS.navy, secondary: ARAGON_COLORS.steel },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: ARAGON_FONTS.primary, fontWeight: 400, letterSpacing: "-0.035em" },
    h2: { fontFamily: ARAGON_FONTS.primary, fontWeight: 400, letterSpacing: "-0.03em" },
    h3: { fontFamily: ARAGON_FONTS.primary, fontWeight: 400, letterSpacing: "-0.025em" },
    button: { textTransform: "none", fontWeight: 700 },
  },
  components: {
    MuiButton: { defaultProps: { disableElevation: true } },
    MuiTextField: { defaultProps: { size: "small" } },
  },
});

export const aragonHomeTheme = createTheme(aragonTheme, {
  typography: {
    fontFamily: ARAGON_FONTS.secondary,
    h1: { fontFamily: ARAGON_FONTS.primary, fontWeight: 400, letterSpacing: "-0.035em" },
    h2: { fontFamily: ARAGON_FONTS.primary, fontWeight: 400, letterSpacing: "-0.03em" },
    h3: { fontFamily: ARAGON_FONTS.primary, fontWeight: 400, letterSpacing: "-0.025em" },
    button: { fontFamily: ARAGON_FONTS.secondary, textTransform: "none", fontWeight: 700 },
  },
});

export function useAragonViewport() {
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 1440 : window.innerWidth,
    height: typeof window === "undefined" ? 900 : window.innerHeight,
  }));
  useLayoutEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const visualViewport = window.visualViewport;
        setViewport({
          width: Math.round(visualViewport?.width || window.innerWidth),
          height: Math.round(visualViewport?.height || window.innerHeight),
        });
      });
    };
    update();
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, []);
  return useMemo(() => {
    const width = viewport.width;
    const height = viewport.height;
    const gutter = d3.scaleLinear().domain([320, 1920]).range([18, 68]).clamp(true)(width);
    const contentWidth = d3.scaleLinear().domain([760, 1920]).range([720, 1360]).clamp(true)(width);
    const authVisualWidth = d3.scaleLinear().domain([960, 1920]).range([0.40, 0.46]).clamp(true)(width);
    const titleSize = d3.scaleLinear().domain([320, 1920]).range([38, 66]).clamp(true)(width);
    const legalTitle = d3.scaleLinear().domain([320, 1920]).range([38, 60]).clamp(true)(width);
    const pageY = d3.scaleLinear().domain([620, 1080]).range([72, 118]).clamp(true)(height);
    return {
      width,
      height,
      compact: width < 760,
      tablet: width < 1080,
      short: height < 720,
      gutter: Math.round(gutter),
      contentWidth: Math.round(contentWidth),
      authVisualWidth,
      titleSize: Math.round(titleSize),
      legalTitle: Math.round(legalTitle),
      pageY: Math.round(pageY),
    };
  }, [viewport]);
}
